import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AuthService } from '../../services/auth/auth.service';
import { GetLanguageService } from '../../services/getLanguage/get-language.service';
import { HelpAssistantService } from '../../services/help-assistant/help-assistant.service';
import {
  AssistantAskClarificationOption,
  AssistantAskResponse,
  AssistantTaxonomyResponse,
  AssistantTaxonomyTopic,
  HelpAssistantApiMessage,
  HelpAssistantMessage,
} from '../../services/help-assistant/help-assistant.types';

/** Small bilingual copy table for widget chrome that isn't part of the ngx-translate catalog
 * (the existing widget already used hardcoded Spanish; this keeps the same low-risk approach
 * while adding English support driven off the app's current language). */
const TEXT = {
  es: {
    welcomeTitle: '¿En qué te ayudo?',
    welcomeBody: 'Pregúntame cómo funciona esta pantalla, un botón o un proceso de PineappleCMS.',
    placeholder: 'Escribe tu pregunta…',
    send: 'Enviar',
    scopeLabel: 'Tema (opcional)',
    scopeAny: 'Cualquier tema',
    citationsTitle: 'Fuentes',
    networkError: 'No pude conectarme con el asistente. Revisa tu conexión.',
    sessionExpired: 'Tu sesión expiró. Inicia sesión nuevamente.',
    forbidden: 'No tienes permiso para usar el asistente en esta sección.',
    unavailable: 'El asistente no está disponible en este momento. Intenta más tarde.',
    generationFailed: 'El asistente no pudo generar una respuesta. Intenta nuevamente.',
    genericError: 'Ocurrió un error inesperado. Intenta nuevamente.',
    fallbackClarify: '¿Sobre cuál de estos temas es tu pregunta?',
    fallbackNoAnswer: 'No pude generar una respuesta con la documentación disponible.',
  },
  en: {
    welcomeTitle: 'How can I help?',
    welcomeBody: 'Ask me how a screen, button or PineappleCMS process works.',
    placeholder: 'Type your question…',
    send: 'Send',
    scopeLabel: 'Topic (optional)',
    scopeAny: 'Any topic',
    citationsTitle: 'Sources',
    networkError: "I couldn't connect to the assistant. Check your connection.",
    sessionExpired: 'Your session expired. Please sign in again.',
    forbidden: "You don't have permission to use the assistant in this section.",
    unavailable: 'The assistant is unavailable right now. Please try again later.',
    generationFailed: "The assistant couldn't generate an answer. Please try again.",
    genericError: 'Something went wrong. Please try again.',
    fallbackClarify: 'Which of these topics is your question about?',
    fallbackNoAnswer: "I couldn't generate an answer from the available documentation.",
  },
} as const;

@Component({
  selector: 'app-help-assistant',
  templateUrl: './help-assistant.component.html',
  styleUrls: ['./help-assistant.component.scss'],
  standalone: false,
})
export class HelpAssistantComponent implements OnInit {
  @ViewChild('messageList') messageList?: ElementRef<HTMLElement>;

  open = false;
  loading = false;
  question = '';
  messages: HelpAssistantMessage[] = [];

  taxonomy: AssistantTaxonomyResponse | null = null;
  selectedTopic = '';

  constructor(
    public readonly auth: AuthService,
    private readonly assistant: HelpAssistantService,
    private readonly language: GetLanguageService,
  ) {}

  ngOnInit(): void {
    if (!this.auth.auth.user) return;
    this.assistant
      .getTaxonomy()
      .then((taxonomy) => (this.taxonomy = taxonomy))
      .catch(() => {
        // The scope selector is a convenience only; if it fails to load the assistant
        // still works without a requested topic/domain hint.
        this.taxonomy = null;
      });
  }

  get locale(): 'en' | 'es' {
    return this.language.currentLanguage === 'en' ? 'en' : 'es';
  }

  get text() {
    return TEXT[this.locale];
  }

  label(labels: { en: string; es: string }): string {
    return labels[this.locale] || labels.es || labels.en;
  }

  async send(): Promise<void> {
    const question = this.question.trim();
    if (!question || this.loading) return;
    this.question = '';
    await this.runAsk(question, true, { requestedTopic: this.selectedTopic || undefined });
  }

  async selectClarificationOption(message: HelpAssistantMessage, option: AssistantAskClarificationOption): Promise<void> {
    if (!message.clarification || this.loading) return;
    const originalQuestion = message.clarification.originalQuestion;
    message.clarification = { ...message.clarification, selectedTopic: option.topic };
    await this.runAsk(originalQuestion, false, { requestedTopic: option.topic });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void this.send();
    }
  }

  trackTopic(_index: number, topic: AssistantTaxonomyTopic): string {
    return topic.topic;
  }

  private async runAsk(
    question: string,
    pushUserMessage: boolean,
    scope: { requestedTopic?: string; requestedDomain?: string },
  ): Promise<void> {
    if (pushUserMessage) {
      this.messages.push({ role: 'user', content: question });
    }
    this.loading = true;
    this.scrollToBottom();

    try {
      const response = await this.assistant.ask({
        question,
        history: this.historyForApi(),
        requestedTopic: scope.requestedTopic,
        requestedDomain: scope.requestedDomain,
        locale: this.locale,
      });
      this.messages.push(this.toMessage(response, question));
    } catch (error: unknown) {
      this.messages.push({ role: 'assistant', content: this.errorMessageFor(error) });
    } finally {
      this.loading = false;
      this.scrollToBottom();
    }
  }

  private toMessage(response: AssistantAskResponse, question: string): HelpAssistantMessage {
    if (response.status === 'ANSWERED') {
      return {
        role: 'assistant',
        content: response.answer || this.text.fallbackNoAnswer,
        citations: response.citations?.length ? response.citations : undefined,
      };
    }

    if (response.status === 'CLARIFY') {
      return {
        role: 'assistant',
        content: response.message || this.text.fallbackClarify,
        clarification: {
          originalQuestion: question,
          options: response.clarification?.topics || [],
        },
      };
    }

    // NO_MATCH, OUT_OF_SCOPE, UNCLEAR all rely on the backend's pre-approved bilingual copy.
    return { role: 'assistant', content: response.message || this.text.fallbackNoAnswer };
  }

  private historyForApi(): HelpAssistantApiMessage[] {
    return this.messages.slice(-6).map((message) => ({ role: message.role, content: message.content }));
  }

  private errorMessageFor(error: any): string {
    const status = error?.status;
    if (status === 401) return this.text.sessionExpired;
    if (status === 403) return this.text.forbidden;
    if (status === 503) return this.text.unavailable;
    if (status === 502) return this.text.generationFailed;
    if (status === 0 || status === undefined || status === null) return this.text.networkError;
    return error?.error?.message || this.text.genericError;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const element = this.messageList?.nativeElement;
      if (element) element.scrollTop = element.scrollHeight;
    });
  }
}

